import { Events } from '@/services/Events';
import { useConfigStore } from '@/store/config.store';
import { useTemplateStore } from '@/store/template.store';
import { getTextFieldFirstCharacterCoordinate } from '@/utils/textFieldCoordinate';
import { getPartialTextSelection } from '@/utils/textSelection';
import {
    updateAlignment,
    updateAngle,
    updateFontColor,
    updateFontFamily,
    updateFontSize,
    updateFontStyle,
    updateFontWeight,
    updateUnderline,
    restoreTextEditingFocus,
} from '@/commons/fabricjs/FabricTextHelper';
import { computed, onMounted, onUnmounted, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { ITextboxField } from '@/typescript/interfaces/ITextboxField';
import type { WFabricWorld } from '@/littleWedding/WFabricWorld';
import type { IFabricTextCustom } from '@/typescript/interfaces/IFabricTextCustom';
import type { ITextboxCustom } from '@/typescript/interfaces/ITextboxCustom';
import type { ETextAlignement } from '@/typescript/enums/ETextAlignement';
import type { CompleteTextStyleDeclaration } from 'fabric';

function useTextField(field: Ref<ITextboxField>, fabricWorld: WFabricWorld) {
    const templateStore = useTemplateStore();
    const { isMobile } = storeToRefs(useConfigStore());

    onMounted(() => {
        Events.on('textbox-transformed', updateTextFieldTransform);
    });

    onUnmounted(() => {
        Events.off('textbox-transformed', updateTextFieldTransform);
    });

    function getFabricText(): IFabricTextCustom | ITextboxCustom | undefined {
        const fText = fabricWorld.getFabricElementById(field.value.id);
        if (!fText || !['text', 'textbox'].includes(fText.type)) {
            return undefined;
        }
        return fText as IFabricTextCustom | ITextboxCustom;
    }

    // A selection covering the whole text is a global style change: it goes on the field and at object
    // level, not per character, otherwise fabric drops it as soon as the text is deleted.
    function getPartialSelection(fText: IFabricTextCustom | ITextboxCustom) {
        return getPartialTextSelection(fText._text.length, templateStore.selectedTextIndexes);
    }

    // Toolbar buttons steal the DOM focus from the text being edited: give it back so the user can keep
    // typing. Desktop only, on mobile the controls live in popups and refocusing would reopen the keyboard.
    function keepEditingFocus(fText: IFabricTextCustom | ITextboxCustom) {
        if (isMobile.value) {
            return;
        }
        restoreTextEditingFocus(fText);
    }

    const color = computed({
        get() {
            if (templateStore.selectedTextIndexes && field.value.styles) {
                const charCoordinates = getTextFieldFirstCharacterCoordinate(field.value, templateStore.selectedTextIndexes.start);
                if (!field.value.styles[charCoordinates.line]) {
                    return field.value.color;
                }
                const styleOfFirstChar = field.value.styles[charCoordinates.line][charCoordinates.char];
                if (styleOfFirstChar?.fill) {
                    return styleOfFirstChar.fill as string;
                }
            }
            return field.value.color;
        },
        set(newColor: string) {
            const fText = getFabricText();
            if (!fText) return;
            const fieldToEdit = { ...field.value };
            const selectedTextIndexes = getPartialSelection(fText);

            // Update global style only if no selection of characters
            if (!selectedTextIndexes) {
                fieldToEdit.color = newColor;
            }

            const textUpdate = updateFontColor({
                fabricCanvas: fabricWorld.fabricCanvas,
                fText,
                fontColor: newColor,
                selectedTextIndexes,
            });

            if (!textUpdate) {
                return;
            }

            fieldToEdit.styles = textUpdate.styles;

            templateStore.updateField(fieldToEdit);
            keepEditingFocus(fText);
        },
    });

    const font = computed({
        get() {
            if (templateStore.selectedTextIndexes && field.value.styles) {
                const charCoordinates = getTextFieldFirstCharacterCoordinate(field.value, templateStore.selectedTextIndexes.start);
                if (!field.value.styles[charCoordinates.line]) {
                    return field.value.font;
                }

                const styleOfFirstChar = field.value.styles[charCoordinates.line][charCoordinates.char];
                if (styleOfFirstChar?.fontFamily) {
                    return styleOfFirstChar.fontFamily;
                }
            }
            return field.value.font;
        },
        async set(newFont: string) {
            const fText = getFabricText();
            if (!fText) return;
            const fieldToEdit = { ...field.value };
            const selectedTextIndexes = getPartialSelection(fText);

            // Update global style only if no selection of characters
            if (!selectedTextIndexes) {
                fieldToEdit.font = newFont;
            }

            const textUpdate = updateFontFamily({
                fabricCanvas: fabricWorld.fabricCanvas,
                fText,
                fontFamily: newFont,
                selectedTextIndexes,
            });
            if (!textUpdate) {
                return;
            }
            fieldToEdit.styles = textUpdate.styles;

            templateStore.updateField(fieldToEdit);
            keepEditingFocus(fText);
        },
    });

    const isBold = computed({
        get() {
            if (templateStore.selectedTextIndexes && field.value.styles) {
                const charCoordinates = getTextFieldFirstCharacterCoordinate(field.value, templateStore.selectedTextIndexes.start);
                if (!field.value.styles[charCoordinates.line]) {
                    return field.value.isBold;
                }

                const styleOfFirstChar = field.value.styles[charCoordinates.line][charCoordinates.char];
                if (styleOfFirstChar?.fontWeight) {
                    return styleOfFirstChar.fontWeight === ('700' as CompleteTextStyleDeclaration['fontWeight']);
                }
            }
            return field.value.isBold;
        },
        set(bold) {
            const fText = getFabricText();
            if (!fText) return;
            const fieldToEdit = { ...field.value };
            const selectedTextIndexes = getPartialSelection(fText);

            // Update global style only if no selection of characters
            if (!selectedTextIndexes) {
                fieldToEdit.isBold = bold;
            }

            const textUpdate = updateFontWeight({
                fabricCanvas: fabricWorld.fabricCanvas,
                fText,
                fontWeight: bold ? '700' : '300',
                selectedTextIndexes,
            });
            if (!textUpdate) {
                return;
            }
            fieldToEdit.styles = textUpdate.styles;

            templateStore.updateField(fieldToEdit);
            keepEditingFocus(fText);
        },
    });

    const isItalic = computed({
        get() {
            if (templateStore.selectedTextIndexes && field.value.styles) {
                const charCoordinates = getTextFieldFirstCharacterCoordinate(field.value, templateStore.selectedTextIndexes.start);
                if (!field.value.styles[charCoordinates.line]) {
                    return field.value.isItalic;
                }

                const styleOfFirstChar = field.value.styles[charCoordinates.line][charCoordinates.char];
                if (styleOfFirstChar?.fontStyle) {
                    return styleOfFirstChar.fontStyle === ('italic' as CompleteTextStyleDeclaration['fontStyle']);
                }
            }
            return field.value.isItalic;
        },
        set(italic) {
            const fText = getFabricText();
            if (!fText) return;
            const fieldToEdit = { ...field.value };
            const selectedTextIndexes = getPartialSelection(fText);

            // Update global style only if no selection of characters
            if (!selectedTextIndexes) {
                fieldToEdit.isItalic = italic;
            }

            const textUpdate = updateFontStyle({
                fabricCanvas: fabricWorld.fabricCanvas,
                fText,
                fontStyle: italic ? 'italic' : 'normal',
                selectedTextIndexes,
            });
            if (!textUpdate) {
                return;
            }
            fieldToEdit.styles = textUpdate.styles;
            fieldToEdit.transform.position = { x: textUpdate.left, y: textUpdate.top };
            templateStore.updateField(fieldToEdit);
            keepEditingFocus(fText);
        },
    });

    const isUnderlined = computed({
        get() {
            if (templateStore.selectedTextIndexes && field.value.styles) {
                const charCoordinates = getTextFieldFirstCharacterCoordinate(field.value, templateStore.selectedTextIndexes.start);
                if (!field.value.styles[charCoordinates.line]) {
                    return field.value.isUnderlined;
                }

                const styleOfFirstChar = field.value.styles[charCoordinates.line][charCoordinates.char];
                if (styleOfFirstChar?.underline !== undefined) {
                    return styleOfFirstChar.underline;
                }
            }
            return field.value.isUnderlined;
        },
        set(underline) {
            const fText = getFabricText();
            if (!fText) return;
            const fieldToEdit = { ...field.value };
            const selectedTextIndexes = getPartialSelection(fText);

            // Update global style only if no selection of characters
            if (!selectedTextIndexes) {
                fieldToEdit.isUnderlined = underline;
            }

            const textUpdate = updateUnderline({
                fabricCanvas: fabricWorld.fabricCanvas,
                fText,
                underline,
                selectedTextIndexes,
            });
            if (!textUpdate) {
                return;
            }
            fieldToEdit.styles = textUpdate.styles;

            templateStore.updateField(fieldToEdit);
            keepEditingFocus(fText);
        },
    });

    const fontSize = computed({
        get() {
            if (templateStore.selectedTextIndexes && field.value.styles) {
                const charCoordinates = getTextFieldFirstCharacterCoordinate(field.value, templateStore.selectedTextIndexes.start);
                if (!field.value.styles[charCoordinates.line]) {
                    return field.value.fontSize;
                }

                const styleOfFirstChar = field.value.styles[charCoordinates.line][charCoordinates.char];
                if (styleOfFirstChar?.fontSize && typeof styleOfFirstChar.fontSize === 'number') {
                    return styleOfFirstChar.fontSize / 4;
                }
            }
            return field.value.fontSize;
        },
        set(newFontSize: number | string | null | undefined) {
            if (typeof newFontSize !== 'string' && typeof newFontSize !== 'number') {
                return;
            }
            const fText = getFabricText();
            if (!fText) return;
            const fieldToEdit = { ...field.value };
            const selectedTextIndexes = getPartialSelection(fText);

            // Update global style only if no selection of characters
            if (!selectedTextIndexes) {
                fieldToEdit.fontSize = Number(newFontSize);
            }

            const textUpdate = updateFontSize({
                fabricCanvas: fabricWorld.fabricCanvas,
                fText,
                fontSize: Number(newFontSize),
                selectedTextIndexes,
            });
            if (!textUpdate) return;

            fieldToEdit.transform.position = { x: textUpdate.left, y: textUpdate.top };
            fieldToEdit.styles = textUpdate.styles;
            templateStore.updateField(fieldToEdit);

            Events.emit('contextual-menu-wheel');
        },
    });

    const angle = computed({
        get() {
            return field.value.transform.angle;
        },
        set(newAngle: number | string | null | undefined) {
            if (typeof newAngle !== 'string' && typeof newAngle !== 'number') {
                return;
            }
            const fText = getFabricText();
            if (!fText) return;
            updateAngle(fabricWorld.fabricCanvas, fText, Number(newAngle));
            const fieldToEdit = { ...field.value };
            fieldToEdit.transform.angle = fText.angle;
            fieldToEdit.transform.position = { x: fText.left, y: fText.top };
            templateStore.updateField(fieldToEdit);
            Events.emit('contextual-menu-wheel');
        },
    });

    const align = computed({
        get() {
            return field.value.textAlign;
        },
        set(newAlign: ETextAlignement) {
            const fText = getFabricText();
            if (!fText) {
                return;
            }
            templateStore.updateField({ ...field.value, textAlign: newAlign } as ITextboxField);
            updateAlignment(fabricWorld.fabricCanvas, fText as ITextboxCustom, newAlign);
            keepEditingFocus(fText);
        },
    });

    function updateTextFieldTransform(fieldEdited: Partial<ITextboxField>) {
        const fieldToEdit = { ...field.value };
        if (fieldToEdit.id !== fieldEdited.id) return;
        fieldToEdit.fontSize = fieldEdited.fontSize ?? fieldToEdit.fontSize;
        fieldToEdit.transform = { ...(fieldEdited.transform ?? fieldToEdit.transform) };
        fieldToEdit.frameSize = fieldEdited.frameSize ?? fieldToEdit.frameSize;
        fieldToEdit.text = fieldEdited.text ?? fieldToEdit.text;
        fieldToEdit.characterSpacing = fieldEdited.characterSpacing ?? fieldToEdit.characterSpacing;
        fieldToEdit.textAlign = fieldEdited.textAlign ?? fieldToEdit.textAlign;
        // Styles can move from character level to object level while typing (see WTextbox)
        fieldToEdit.font = fieldEdited.font ?? fieldToEdit.font;
        fieldToEdit.color = fieldEdited.color ?? fieldToEdit.color;
        fieldToEdit.isBold = fieldEdited.isBold ?? fieldToEdit.isBold;
        fieldToEdit.isItalic = fieldEdited.isItalic ?? fieldToEdit.isItalic;
        fieldToEdit.isUnderlined = fieldEdited.isUnderlined ?? fieldToEdit.isUnderlined;
        fieldToEdit.styles = fieldEdited.styles ?? fieldToEdit.styles;
        templateStore.updateField(fieldToEdit);
    }

    return { color, font, isBold, isItalic, isUnderlined, fontSize, angle, align };
}

export { useTextField };
